import {Grid} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {FormAutocomplete, FormCustomSwitch, FormDateTimePicker, FormTextField,} from "../../../common/form-fields";
import {useRecoilState} from "recoil";
import "dayjs/locale/it";
import {SelectElementModelFE} from "../../../api/types/generic";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {FormExceptionListModel} from "../../../api/types/exceptionList";
import CustomBanner from "../../../common/components/CustomBanner";
import {CommercialCategorySelectFE} from "../../../api/types/commercialCategory";
import {useFormContext} from "react-hook-form";
import DealerAndTenant from "../../../common/components/DealerAndTenant";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

const FormExceptionList = ({
                               readOnly, inUse, commercialCategoryListOriginal
                           }: {
    readOnly: boolean, inUse: boolean, commercialCategoryListOriginal: CommercialCategorySelectFE[]
}) => {
    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Recoil
    const [commercialSegmentList] = useRecoilState(commercialSegmentListAtom);

    // Use Context
    const {globalData: {user}} = useContext(GlobalContext);

    // Use State
    const [commercialCategoryList, setCommercialCategoryList] = useState<SelectElementModelFE[]>([]);

    // Use Form Context
    const form = useFormContext<FormExceptionListModel>();
    const watchedLegalEntity = form.watch("legalEntity");

    // Effect per filtrare le categorie quando cambia la legal entity o il segment
    useEffect(() => {
        const legalEntityCode = (watchedLegalEntity as SelectElementModelFE)?.code as string
            ?? (user.legalEntities?.length === 1 ? user.legalEntities[0].name : null);

        if (!legalEntityCode || commercialCategoryListOriginal.length === 0) {
            setCommercialCategoryList([]);
            return;
        }

        // Filtra per commercial segment se selezionato
        const segmentValue = form.watch("commercialSegmentId");
        let filtered = commercialCategoryListOriginal;
        if (segmentValue) {
            filtered = commercialCategoryListOriginal.filter(
                cc => cc.commercialSegment.id === (segmentValue as SelectElementModelFE).code
            );
        }

        // Espandi le categorie in base alla legal entity selezionata
        const expandedCategories = new Map<string, CommercialCategorySelectFE>();
        filtered.forEach(category => {
            category.labels?.forEach(labelObj => {
                if (labelObj.country === legalEntityCode) {
                    const key = labelObj.label;
                    if (!expandedCategories.has(key)) {
                        expandedCategories.set(key, {
                            ...category,
                            label: labelObj.label
                        });
                    }
                }
            });
        });
        setCommercialCategoryList(Array.from(expandedCategories.values()));
    }, [watchedLegalEntity, commercialCategoryListOriginal, user.legalEntities]);

    const handleChangeCommercialSegment = async (value: SelectElementModelFE) => {
        form.setValue("commercialSegmentId", value);
        const legalEntityCode = (watchedLegalEntity as SelectElementModelFE)?.code as string
            ?? (user.legalEntities?.length === 1 ? user.legalEntities[0].name : null);

        let filtered = commercialCategoryListOriginal;
        if (value) {
            filtered = commercialCategoryListOriginal.filter(cc => cc.commercialSegment.id === (value as SelectElementModelFE).code);
        }

        if (!legalEntityCode) {
            setCommercialCategoryList([]);
            return;
        }

        const expandedCategories = new Map<string, CommercialCategorySelectFE>();
        filtered.forEach(category => {
            category.labels?.forEach(labelObj => {
                if (labelObj.country === legalEntityCode) {
                    const key = labelObj.label;
                    if (!expandedCategories.has(key)) {
                        expandedCategories.set(key, {
                            ...category,
                            label: labelObj.label
                        });
                    }
                }
            });
        });
        setCommercialCategoryList(Array.from(expandedCategories.values()));
    };

    return (
        <>
            <Grid container spacing={2} alignItems="start">
                {inUse && <CustomBanner message={t("pricing:exceptionList.banner")} severity={"warning"}/>}
                <Grid container item xs={12} md={12} lg={12} spacing={2} rowSpacing={3}>
                    {/* NAME ANCILLARY LIST */}
                    <Grid item xs={12} md={3}>
                        <FormTextField
                            {...form.register("name")}
                            label={`${t("pricing:rentalPeriods.form.labels.name")}*`}
                            readOnly={readOnly || inUse}
                        />
                    </Grid>
                    {/* DESCRIPTION ANCILLARY LIST */}
                    <Grid item xs={12} md={3}>
                        <FormTextField
                            {...form.register("description")}
                            label={`${t("pricing:exceptionList.form.labels.description")}*`}
                            readOnly={readOnly || inUse}
                        />
                    </Grid>
                    {/* COMMERCIAL SEGMENTS */}
                    <Grid item xs={12} md={3}>
                        <FormAutocomplete
                            {...form.register("commercialSegmentId")}
                            label={`${t("pricing:exceptionList.form.labels.commercialSegment")}*`}
                            options={commercialSegmentList}
                            isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                            readOnly={readOnly || inUse}
                            handleChange={handleChangeCommercialSegment}
                        />
                    </Grid>

                    {/* COMMERCIAL CATEGORY */}
                    <Grid item xs={12} md={3}>
                        <FormAutocomplete
                            {...form.register("commercialCategoryId")}
                            label={`${t("pricing:exceptionList.form.labels.commercialCategory")}*`}
                            options={commercialCategoryList}
                            isOptionEqualToValue={(option: SelectElementModelFE, value: SelectElementModelFE) => option?.code === value?.code}
                            readOnly={readOnly || inUse}
                        />
                    </Grid>
                    {/* RANGE ANCILLARY LIST */}
                    {/* FROM */}
                    <Grid item xs={12} md={3}>
                        <FormDateTimePicker
                            {...form.register("from")}
                            label={`${t("pricing:exceptionList.form.labels.startDateAssociation")}*`}
                            readOnly={readOnly || inUse}
                        />
                    </Grid>
                    {/* TO */}
                    <Grid item xs={12} md={3}>
                        <FormDateTimePicker
                            {...form.register("to")}
                            label={`${t("pricing:exceptionList.form.labels.endDateAssociation")}*`}
                            readOnly={readOnly}
                        />
                    </Grid>
                    <DealerAndTenant
                        readOnly={readOnly}
                        md={3}
                        legalEntityName={"legalEntity"}
                        dealerName={"dealer"}
                        dealerIsVisible={true}
                        idForReadOnly={form.getValues("id")}
                    />
                    {/* ancillaryPack */}
                    <Grid item xs={12} md={3}>
                        <FormCustomSwitch
                            {...form.register("ancillaryPack")}
                            label="Pack"
                            readOnly={readOnly || inUse || Boolean(form.getValues("id"))}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
};

export default FormExceptionList;
